import { useCallback, useEffect, useState } from 'react';
import client, { mensajeDeError } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { Campo, Etiqueta, Entrada, Selector, MensajeError } from '../../components/ui/Form';
import { fechaLegible } from '../../utils/fechas';
import { TituloSeccion, BarraAcciones, TablaEnvoltura, Tabla, CeldaAcciones, SinDatos } from './comunes';

function ClientesAdmin() {
  const [clientes, setClientes] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [paquetes, setPaquetes] = useState([]);
  const [gestionando, setGestionando] = useState(null); // cliente al que se le gestiona paquete
  const [paqueteId, setPaqueteId] = useState('');
  const [clasesRestantes, setClasesRestantes] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    client.get('/paquetes').then(({ data }) => setPaquetes(data)).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    client
      .get('/admin/clientes', { params: busqueda ? { busqueda } : {} })
      .then(({ data }) => setClientes(data))
      .catch(() => setClientes([]));
  }, [busqueda]);

  useEffect(() => {
    const t = setTimeout(cargar, 300); // espera a que dejen de teclear
    return () => clearTimeout(t);
  }, [cargar]);

  const abrir = (cliente) => {
    setError('');
    setPaqueteId('');
    setClasesRestantes(cliente.clases_restantes ?? '');
    setGestionando(cliente);
  };

  const asignar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError('');
    try {
      if (paqueteId) {
        await client.post(`/admin/clientes/${gestionando.id}/paquete`, { paqueteId });
      } else if (gestionando.paquete_cliente_id && clasesRestantes !== '') {
        await client.patch(`/admin/clientes/paquete/${gestionando.paquete_cliente_id}`, {
          clasesRestantes: Number(clasesRestantes),
        });
      }
      setGestionando(null);
      cargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <TituloSeccion>Clientes</TituloSeccion>
      <BarraAcciones>
        <Entrada
          type="search"
          placeholder="Buscar por nombre o email…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          aria-label="Buscar clientes"
          style={{ maxWidth: 320 }}
        />
      </BarraAcciones>
      {error && !gestionando && <MensajeError role="alert">{error}</MensajeError>}

      {!clientes ? (
        <Spinner />
      ) : (
        <TablaEnvoltura>
          {clientes.length === 0 ? (
            <SinDatos>No se encontraron clientes.</SinDatos>
          ) : (
            <Tabla>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Contacto</th>
                  <th>Paquete activo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c) => (
                  <tr key={c.id}>
                    <td>{c.nombre}</td>
                    <td>
                      {c.email}
                      {c.telefono ? ` · ${c.telefono}` : ''}
                    </td>
                    <td>
                      {c.paquete_nombre ? (
                        <>
                          <Badge $tono="sage">{c.paquete_nombre}</Badge>{' '}
                          <Badge $tono="arena">
                            {c.paquete_tipo === 'ilimitado'
                              ? `Vence ${fechaLegible(c.fecha_vencimiento)}`
                              : `${c.clases_restantes} restantes`}
                          </Badge>
                        </>
                      ) : (
                        <Badge $tono="peligro">Sin paquete</Badge>
                      )}
                    </td>
                    <CeldaAcciones>
                      <Button $variante="secundario" $chico onClick={() => abrir(c)}>
                        Gestionar paquete
                      </Button>
                    </CeldaAcciones>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}
        </TablaEnvoltura>
      )}

      <Modal
        abierto={!!gestionando}
        titulo={`Paquete de ${gestionando?.nombre || ''}`}
        onCerrar={() => setGestionando(null)}
      >
        {gestionando && (
          <form onSubmit={asignar}>
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Campo>
              <Etiqueta htmlFor="cl-paquete">Asignar paquete nuevo (ej. pago en efectivo)</Etiqueta>
              <Selector id="cl-paquete" value={paqueteId} onChange={(e) => setPaqueteId(e.target.value)}>
                <option value="">No asignar nuevo</option>
                {paquetes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} — ${Number(p.precio).toLocaleString('es-MX')}
                  </option>
                ))}
              </Selector>
            </Campo>
            {gestionando.paquete_cliente_id && gestionando.paquete_tipo === 'por_clases' && !paqueteId && (
              <Campo>
                <Etiqueta htmlFor="cl-restantes">Ajustar clases restantes del paquete actual</Etiqueta>
                <Entrada
                  id="cl-restantes"
                  type="number"
                  min="0"
                  value={clasesRestantes}
                  onChange={(e) => setClasesRestantes(e.target.value)}
                />
              </Campo>
            )}
            <Button type="submit" $completo disabled={guardando}>
              {guardando ? 'Guardando…' : 'Aplicar cambios'}
            </Button>
          </form>
        )}
      </Modal>
    </>
  );
}

export default ClientesAdmin;
